import React, { useEffect, useMemo, useState } from 'react'
import dayjs from 'dayjs'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import Chip from '@mui/material/Chip'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'
import apiRequest from '../../../lib/http'
import { businessTimeZone } from '../../../utils/date'
import { labelMap } from '../../../constants/gatewayRegistry'

// WS 2122976. The monthly NACHA return-rate report (vlg-server GET /payments/nacha/return-rates,
// services/payments/nachaReturnRate.js) only ever reached Sentry. This shows it as the server
// computes it: bank debits over two closed calendar months, per gateway. Every judgement (status,
// the "up to" ceiling, what the numbers cannot see) comes from the report, never from here.
// MUI controls throughout, like the To Process tab beside it, so the dark theme applies.

const STATUS = {
  WITHIN_LIMIT: { text: 'Within limit', color: 'success' },
  EXCEEDED: { text: 'Over the level', color: 'warning' },
  BREACHED: { text: 'Over the threshold', color: 'error' },
  INDETERMINATE: { text: "Can't tell", color: 'default' },
  NO_ENTRIES: { text: 'No debits', color: 'default' },
}

const RATE_COLUMNS = [
  { key: 'unauthorized', title: 'Unauthorized' },
  { key: 'administrative', title: 'Administrative' },
  { key: 'overall', title: 'Overall' },
]

// The closed two-month periods, newest first, in business time. A period is named by its last
// month, which is what the endpoint takes as ?month=.
export const closedMonthOptions = (now = dayjs().tz(businessTimeZone), count = 6) => {
  const thisMonth = dayjs(now).tz(businessTimeZone).startOf('month')
  return Array.from({ length: count }, (_, i) => {
    const last = thisMonth.subtract(i + 1, 'month')
    const first = last.subtract(1, 'month')
    return { value: last.format('YYYY-MM'), label: `${first.format('MMM')} – ${last.format('MMM YYYY')}` }
  })
}

const gatewayName = (id) => {
  if (id === 'ALL') return 'All gateways'
  if (!id || id === 'unknown') return 'No gateway recorded'
  return labelMap()[id] || id
}

const count = (n) => Number(n || 0).toLocaleString('en-US')

const rateOf = (originator, key) => [originator?.threshold, ...(originator?.levels || [])].find((r) => r?.key === key)

const RateCell = ({ rate }) => {
  if (!rate) return <TableCell>—</TableCell>
  const status = STATUS[rate.status] || { text: rate.status, color: 'default' }
  return (
    <TableCell sx={{ verticalAlign: 'top' }}>
      <div className="flex flex-col items-start gap-1">
        <span>{rate.rateLabel ?? '—'}</span>
        <Chip size="small" label={status.text} color={status.color} variant={status.color === 'default' ? 'outlined' : 'filled'} />
        {rate.status === 'INDETERMINATE' && <span className="text-xs text-text-secondary">up to {rate.ceilingRateLabel}</span>}
      </div>
    </TableCell>
  )
}

const errorMessage = (error) => error?.response?.data?.error?.errors?.[0]?.message || 'Could not load the return rates'

const ReturnRates = ({ now }) => {
  const options = useMemo(() => closedMonthOptions(now), [now])
  const [month, setMonth] = useState(options[0].value)
  const [report, setReport] = useState(null)
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let live = true
    setLoading(true)
    setError(null)
    apiRequest.get('/payments/nacha/return-rates', { params: { month } })
      .then((res) => { if (live) setReport(res?.data?.data || null) })
      .catch((e) => { if (live) { setReport(null); setError(errorMessage(e)) } })
      .finally(() => { if (live) setLoading(false) })
    return () => { live = false }
  }, [month])

  const rows = report ? [...(report.originators || []), report.combined].filter(Boolean) : []

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col">
        <label id="return-rates-period-label" className="block text-xs font-medium text-text-secondary mb-1">Period</label>
        <Select
          size="small"
          labelId="return-rates-period-label"
          value={month}
          onChange={(e) => setMonth(e.target.value)}
          sx={{ minWidth: 160, alignSelf: 'flex-start' }}
        >
          {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </Select>
      </div>

      <p className="text-sm text-text-secondary">
        Bank debits only, counted per gateway over two closed calendar months. A rate is a minimum: returns that came back
        without a bank reason could push it higher, which is when the status reads &quot;Can&apos;t tell&quot;.
      </p>

      {loading && <p className="text-sm">Loading…</p>}
      {!loading && error && <p className="text-sm text-primary-static-error-600">{error}</p>}

      {!loading && !error && report && (
        <div className="overflow-x-auto">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Gateway</TableCell>
                <TableCell>Bank debits</TableCell>
                <TableCell>Returns</TableCell>
                {RATE_COLUMNS.map((c) => <TableCell key={c.key}>{`${c.title} (limit ${rateOf(report.combined, c.key)?.limitLabel ?? '—'})`}</TableCell>)}
                <TableCell>Returns without a bank reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((o) => (
                <TableRow key={o.originator} sx={o.originator === 'ALL' ? { '& td': { fontWeight: 600 } } : undefined}>
                  <TableCell>{gatewayName(o.originator)}</TableCell>
                  <TableCell>{count(o.denominator?.debitEntries)}</TableCell>
                  <TableCell>{count(o.returns?.observed)}</TableCell>
                  {RATE_COLUMNS.map((c) => <RateCell key={c.key} rate={rateOf(o, c.key)} />)}
                  <TableCell>{count(o.threshold?.unknownReturns)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {!loading && !error && report?.coverage?.cannotSee?.length > 0 && (
        <details className="text-sm text-text-secondary">
          <summary className="cursor-pointer">What these numbers cannot see</summary>
          <ul className="mt-2 list-disc pl-5">
            {report.coverage.cannotSee.map((line) => <li key={line}>{line}</li>)}
          </ul>
        </details>
      )}
    </div>
  )
}

export default ReturnRates
